import type { ModelId, ProjectId, ProjectMemberId, TenantId } from '@/shared/types/common.types';
import type { HealthStatus } from './rollup.types';

/**
 * What the deployed Core service supports, from `GET /api/v1/capabilities`.
 *
 * Every v1 page is shown only when its flag is on. A flag added to Core later is absent
 * from an older deployment's response and reads as off.
 */
export interface Capabilities {
  readonly projectWorkspace: boolean;
  readonly projectDocuments: boolean;
  readonly documentTypes: readonly string[];
  readonly uploadLimits: readonly UploadLimit[];
  readonly partSizeBytes: number;
  readonly mergeModelRequests: boolean;
  readonly modelFileMaxSizeBytes: number;
  readonly assistant: boolean;
  /** `GET /projects/{id}/summary`: the Overview dashboard. */
  readonly projectSummary: boolean;
  /** `GET /projects/{id}/health`: the Health tab. */
  readonly projectHealth: boolean;
  /** `GET /projects/{id}/audits`: the Audits tab. */
  readonly projectAudits: boolean;
  /** Key-value properties and milestones of a project and its models. */
  readonly projectDetails: boolean;
}

export interface UploadLimit {
  readonly documentType: string;
  readonly extensions: readonly string[];
  readonly maxSizeBytes: number;
}

export const PROJECT_ROLES = ['Viewer', 'Contributor', 'DocumentController', 'ProjectAdmin'] as const;
/** `Unknown` when a newer Core sends a role this client does not know yet. */
export type ProjectRole = (typeof PROJECT_ROLES)[number] | 'Unknown';

/**
 * Where a project is in its life. Proposed Core field (docs/project-admin-and-list-summary.md); a
 * Core that does not send it leaves it `null`, and `Unknown` is a value this client does not know.
 */
export const PROJECT_STATUSES = ['Active', 'OnHold', 'Completed', 'Archived'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number] | 'Unknown';

/** A linked model's state within one project, after the ISO 19650 container states. */
export const MODEL_STATUSES = ['WorkInProgress', 'Shared', 'Published', 'Archived'] as const;
export type ModelStatus = (typeof MODEL_STATUSES)[number] | 'Unknown';

export const TENANT_ROLES = ['TenantAdmin', 'Member'] as const;
export type TenantRole = (typeof TENANT_ROLES)[number] | 'Unknown';

export const MEMBER_TYPES = ['Organisation', 'External'] as const;
export type MemberType = (typeof MEMBER_TYPES)[number];

/** What a person does on a project. Free text on the wire; these are the suggestions. */
export const MEMBER_POSITIONS = [
  'Project Manager',
  'Design Manager',
  'BIM Coordinator',
  'Engineer',
  'Architect',
  'Consultant',
  'Client Representative',
  'Contractor',
] as const;

export const MEMBER_STATUSES = ['Pending', 'Active'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number] | 'Unknown';

/** A person as Core records them on a write: the token subject and the name it carried. */
export interface PersonRef {
  readonly subject: string;
  readonly displayName: string;
}

export interface Tenant {
  readonly id: TenantId;
  readonly name: string;
  readonly myRole: TenantRole;
}

export interface WorkspaceProjectSummary {
  readonly id: ProjectId;
  readonly tenantId: TenantId;
  readonly code: string;
  readonly name: string;
  readonly myRole: ProjectRole;
  readonly createdAt: string;
  /** `null` when this Core does not report a status. */
  readonly status: ProjectStatus | null;
  /** Models linked to the project; `null` when this Core's project list does not report it. */
  readonly modelCount: number | null;
  /** The project's overall health roll-up; `null` when Core does not report it on the list. */
  readonly health: HealthStatus | null;
  /** The person who leads the project; `null` when Core names nobody or does not report it. */
  readonly manager: PersonRef | null;
}

/**
 * A head model linked to a project, with its current state. Core reads it from the
 * design-model database on every request, so a new snapshot shows without relinking.
 */
export interface LinkedModel {
  readonly id: ModelId;
  readonly title: string;
  readonly modelVersion: number;
  /** The model's state in this project; `null` when this Core does not report it. */
  readonly status: ModelStatus | null;
  /** `null` when the model has never been synchronised. */
  readonly lastSynchronized: string | null;
  /** The project member answerable for this model; `null` when nobody is set or Core does not say. */
  readonly responsible: PersonRef | null;
  /** Snapshots the model has; `null` when Core does not say. */
  readonly snapshotCount: number | null;
}

export interface WorkspaceProject extends WorkspaceProjectSummary {
  readonly canViewRestricted: boolean;
  readonly modelIds: readonly ModelId[];
  /**
   * Details of the linked models. A model Core can no longer find is in `modelIds` but not
   * here, and an older Core sends none at all.
   */
  readonly models: readonly LinkedModel[];
  readonly activeManifestRevisionId: string | null;
  /** Concurrency token; sent back as `If-Match` on writes. */
  readonly etag: string;
  readonly createdBy: PersonRef;
}

/**
 * A project membership. A member added by email stays `Pending`, with no subject or name,
 * until that person first calls Core with a verified email claim.
 */
export interface ProjectMember {
  readonly id: ProjectMemberId;
  readonly email: string;
  readonly subject: string | null;
  readonly displayName: string | null;
  readonly role: ProjectRole;
  readonly canViewRestricted: boolean;
  readonly status: MemberStatus;
  readonly addedAt: string;
  /** What they do on this project; `null` when not set or Core does not say. */
  readonly position: string | null;
  /** `null` when Core does not say. */
  readonly memberType: MemberType | null;
}

/** One page of a v1 collection. `nextCursor` is opaque and `null` on the last page. */
export interface CursorPage<T> {
  readonly items: readonly T[];
  readonly nextCursor: string | null;
}

export interface ProjectListPage extends CursorPage<WorkspaceProjectSummary> {
  readonly permissions: { readonly canCreateProject: boolean };
}

export interface CreateProjectInput {
  readonly tenantId: TenantId;
  readonly code: string;
  readonly name: string;
}

/** Adding a member by email; they stay Pending until they first sign in with that email verified. */
export interface AddMemberInput {
  readonly email: string;
  readonly role: Exclude<ProjectRole, 'Unknown'>;
  readonly canViewRestricted: boolean;
  readonly position?: string;
  readonly memberType?: MemberType;
}

/** What a project admin may change; Core needs at least one of them. */
export interface UpdateProjectInput {
  readonly name?: string;
  readonly code?: string;
  readonly status?: Exclude<ProjectStatus, 'Unknown'>;
}

export interface UpdateMemberInput {
  readonly role?: Exclude<ProjectRole, 'Unknown'>;
  readonly position?: string | null;
  readonly canViewRestricted?: boolean;
}

export interface ProjectListQuery {
  readonly q?: string;
  readonly cursor?: string;
  readonly limit?: number;
}
